import React from 'react';
import FionaFields from './FionaFields/FionaFields';
import ComoFields from './ComoFields/ComoFields';
import ProcinemaFields from './ProcinemaFields/ProcinemaFields';
import OwnItFields from './OwnItFields/OwnItFields';
import ShiftFields from './ShiftFields/ShiftFields';
import CinechFields from './CinechFields/CinechFields';
import MaccsboxFields from './MaccsboxFields/MaccsboxFields';
import WordpressFields from './WordpressFields/WordpressFields';
import EventivalFields from './EventivalFields/EventivalFields';
import MailchimpFields from './MailchimpFields/MailchimpFields';
import BrevoFields from './BrevoFields/BrevoFields';
import GetResponseFields from './GetResponseFields/GetResponseFields';
import PantaflixFields from './PantaflixFields/PantaflixFields';
import KronosImportFields from './KronosImportFields/KronosImportFields';
import WeservFields from './WeservFields/WeservFields';
import JsonImportFields from './JsonImportFields/JsonImportFields';
import OzuFields from './OzuFields/OzuFields';
import GoogleRecaptchaFields from './GoogleRecaptchaFields/GoogleRecaptchaFields';
import GoogleTagManagerFields from './GoogleTagManagerFields/GoogleTagManagerFields';
import PostHogFields from './PostHogFields/PostHogFields';
import MaxmindFields from './MaxmindFields/MaxmindFields';

import pantaflix from '../../assets/images/integrations/pantaflix.webp';
import recaptcha from '../../assets/images/integrations/recaptcha.webp';
import mailchimp from '../../assets/images/integrations/mailchimp.webp';
import brevo from '../../assets/images/integrations/brevo.webp';
import getresponse from '../../assets/images/integrations/getresponse.webp';
import eventival from '../../assets/images/integrations/eventival.webp';
import maccsbox from '../../assets/images/integrations/maccsbox.webp';
import shift72 from '../../assets/images/integrations/shift72.webp';
import cinech from '../../assets/images/integrations/cinech.webp';
import kronos from '../../assets/images/integrations/kronos.webp';
import weserv from '../../assets/images/integrations/weserv.webp';
import plausible from '../../assets/images/integrations/plausible.webp';
import PlausibleFields from './PlausibleFields/PlausibleFields';
import fiona from '../../assets/images/integrations/fiona.webp';
import own_it from '../../assets/images/integrations/own_it.webp';
import json from '../../assets/images/integrations/json.webp';
import ozu from '../../assets/images/integrations/ozu.webp';
import como from '../../assets/images/integrations/como.webp';
import procinema from '../../assets/images/integrations/procinema.webp';
import gtm from '../../assets/images/integrations/gtm.webp';
import posthog from '../../assets/images/integrations/posthog.webp';
import wp from '../../assets/images/integrations/wp.webp';
import maxmind from '../../assets/images/integrations/maxmind.webp';

export const INTEGRATION_CATEGORIES = ['import', 'streaming', 'marketing', 'web'];

const screenings_import_enabled = (integration) => (
    !!integration?.import?.screenings?.enabled
);

const tickets_import_enabled = (integration) => (
    !!integration?.import?.tickets?.enabled
);

const non_empty = (value) => (
    value !== undefined && value !== null && String(value).trim() !== ''
);

const build_screenings_flags = (screenings, t, { include_fullfill_people = false } = {}) => {
    const flags = [];

    if (screenings?.delete_future)
        flags.push(t('settings.integrations.status.flag_delete_future'));
    if (screenings?.delete_existing)
        flags.push(t('settings.integrations.status.flag_delete_existing'));
    if (include_fullfill_people && screenings?.fullfill_people)
        flags.push(t('settings.integrations.status.flag_fullfill_people'));

    return flags;
};

const append_flags = (summary, flags, t) => {
    if (!flags.length)
        return summary;

    return `${summary}${t('settings.integrations.status.flags_paren', { flags: flags.join(', ') })}`;
};

const import_screenings_status_summary = (integration, t, { include_fullfill_people = false } = {}) => {
    const has_screenings = screenings_import_enabled(integration);
    const has_tickets = tickets_import_enabled(integration);
    const screenings = integration?.import?.screenings;
    const flags = has_screenings
        ? build_screenings_flags(screenings, t, { include_fullfill_people })
        : [];

    if (has_screenings && has_tickets)
        return append_flags(
            t('settings.integrations.status.screenings_and_tickets_import'),
            flags,
            t
        );
    if (has_screenings)
        return append_flags(t('settings.integrations.status.screenings_import'), flags, t);
    if (has_tickets)
        return t('settings.integrations.status.tickets_import');

    return t('settings.integrations.status.configured');
};

const integration_missing_status_key = (key) => {
    switch (key) {
        case 'json':
        case 'cinech':
            return 'url_missing';
        case 'maccsbox':
            return 'ftp_missing';
        case 'gtm':
            return 'gtm_missing';
        case 'posthog':
            return 'posthog_missing';
        case 'recaptcha':
            return 'recaptcha_missing';
        case 'weserv':
            return 'proxy_host_missing';
        case 'plausible':
            return 'plausible_disabled';
        case 'wp':
            return 'wp_empty';
        default:
            return 'api_key_missing';
    }
};

const newsletter_provider_configured = (integration) => (
    non_empty(integration?.newsletter?.api_key)
        || non_empty(integration?.survey?.api_key)
);

const newsletter_provider_status_summary = (integration, t) => {
    const has_newsletter = non_empty(integration?.newsletter?.api_key);
    const has_survey = non_empty(integration?.survey?.api_key);

    if (has_newsletter && has_survey)
        return t('settings.integrations.status.newsletter_and_survey');
    if (has_newsletter)
        return t('settings.integrations.status.newsletter');
    if (has_survey)
        return t('settings.integrations.status.survey');

    return t('settings.integrations.status.api_key_missing');
};

const is_integration_configured = (key, integration) => {
    if (!integration)
        return false;

    switch (key) {
        case 'wp':
            return (integration.websites || []).length > 0;
        case 'kronos':
        case 'eventival':
        case 'fiona':
        case 'shift72':
        case 'pantaflix':
        case 'own_it':
        case 'como':
            return non_empty(integration.api_key);
        case 'procinema':
            return non_empty(integration.api_key) && non_empty(integration.cinema_site_id);
        case 'json':
            return non_empty(integration.url) && non_empty(integration.x_api_key);
        case 'cinech':
            return non_empty(integration.url);
        case 'ozu':
            return true;
        case 'maccsbox':
            return non_empty(integration.ftp?.hostname);
        case 'gtm':
            return non_empty(integration.id);
        case 'posthog':
            return non_empty(integration.personal_api_key)
                || non_empty(integration.project_api_key);
        case 'recaptcha':
            return non_empty(integration.public_key) && non_empty(integration.secret_key);
        case 'weserv':
            return non_empty(integration.proxy_img_host);
        case 'plausible':
            return integration.enabled !== false;
        case 'maxmind':
            return non_empty(integration.database_path);
        case 'mailchimp':
        case 'brevo':
        case 'getresponse':
            return newsletter_provider_configured(integration);
        default:
            return true;
    }
};

const import_integration_credential_missing = (key, integration, t) => {
    if (is_integration_configured(key, integration))
        return null;

    if (key === 'json') {
        if (!non_empty(integration.url))
            return t('settings.integrations.status.url_missing');
        return t('settings.integrations.status.api_key_missing');
    }

    return t(`settings.integrations.status.${integration_missing_status_key(key)}`);
};

const integration_status_summary = (key, integration, t) => {
    if (!integration)
        return '';

    switch (key) {
        case 'wp': {
            const websites = integration.websites || [];
            const count = websites.length;
            if (count === 0)
                return t('settings.integrations.status.wp_empty');

            const url = websites[0]?.uri || websites[0]?.name || '';
            return t('settings.integrations.status.wp_site', { count, url });
        }
        case 'eventival':
        case 'fiona':
        case 'kronos':
        case 'json':
        case 'ozu':
        case 'cinech': {
            const credential_missing = import_integration_credential_missing(key, integration, t);
            if (credential_missing)
                return credential_missing;

            return import_screenings_status_summary(integration, t, {
                include_fullfill_people: key === 'eventival',
            });
        }
        case 'maccsbox':
            return integration.ftp?.hostname
                ? t('settings.integrations.status.ftp_configured')
                : t('settings.integrations.status.ftp_missing');
        case 'gtm':
            return integration.id
                ? t('settings.integrations.status.gtm_configured')
                : t('settings.integrations.status.gtm_missing');
        case 'posthog':
            return (non_empty(integration.personal_api_key)
                || non_empty(integration.project_api_key))
                ? t('settings.integrations.status.posthog_configured')
                : t('settings.integrations.status.posthog_missing');
        case 'mailchimp':
        case 'brevo':
        case 'getresponse':
            return newsletter_provider_status_summary(integration, t);
        case 'recaptcha':
            return integration.public_key
                ? t('settings.integrations.status.recaptcha_configured')
                : t('settings.integrations.status.recaptcha_missing');
        case 'shift72':
        case 'pantaflix':
        case 'own_it':
        case 'como':
        case 'procinema':
            return integration.api_key
                ? t('settings.integrations.status.api_configured')
                : t('settings.integrations.status.api_key_missing');
        case 'weserv':
            if (!integration.proxy_img_host)
                return t('settings.integrations.status.proxy_host_missing');
            return t('settings.integrations.status.proxy_enabled', {
                host: integration.proxy_img_host,
            });
        case 'plausible':
            return integration.enabled === false
                ? t('settings.integrations.status.plausible_disabled')
                : t('settings.integrations.status.plausible_enabled', {
                    host: integration.host || 'analytics.netoxygen.ch',
                });
        case 'maxmind':
            return non_empty(integration.database_path)
                ? t('settings.integrations.status.maxmind_configured')
                : t('settings.integrations.status.maxmind_db_missing');
        default:
            return t('settings.integrations.status.configured');
    }
};

export const INTEGRATION_CATALOG = [
    {
        key: 'eventival',
        category: 'import',
        image: eventival,
        fieldsComponent: EventivalFields,
        superadminOnly: false,
        tags: ['import'],
    },
    {
        key: 'fiona',
        category: 'import',
        image: fiona,
        fieldsComponent: FionaFields,
        superadminOnly: false,
        tags: ['import'],
    },
    {
        key: 'kronos',
        category: 'import',
        image: kronos,
        fieldsComponent: KronosImportFields,
        superadminOnly: false,
        tags: ['import'],
    },
    {
        key: 'json',
        category: 'import',
        image: json,
        fieldsComponent: JsonImportFields,
        superadminOnly: false,
        tags: ['import'],
    },
    {
        key: 'ozu',
        category: 'import',
        image: ozu,
        fieldsComponent: OzuFields,
        superadminOnly: false,
        tags: ['import'],
    },
    {
        key: 'cinech',
        category: 'import',
        image: cinech,
        fieldsComponent: CinechFields,
        superadminOnly: false,
        tags: ['import'],
    },
    {
        key: 'maccsbox',
        category: 'import',
        image: maccsbox,
        fieldsComponent: MaccsboxFields,
        superadminOnly: false,
        tags: ['export'],
    },
    {
        key: 'shift72',
        category: 'streaming',
        image: shift72,
        fieldsComponent: ShiftFields,
        superadminOnly: false,
        tags: ['streaming'],
    },
    {
        key: 'pantaflix',
        category: 'streaming',
        image: pantaflix,
        fieldsComponent: PantaflixFields,
        superadminOnly: false,
        tags: ['streaming'],
    },
    {
        key: 'own_it',
        category: 'streaming',
        image: own_it,
        fieldsComponent: OwnItFields,
        superadminOnly: false,
        tags: ['streaming'],
    },
    {
        key: 'como',
        category: 'marketing',
        image: como,
        fieldsComponent: ComoFields,
        superadminOnly: false,
        tags: [],
    },
    {
        key: 'procinema',
        category: 'marketing',
        image: procinema,
        fieldsComponent: ProcinemaFields,
        superadminOnly: false,
        tags: ['voucher'],
    },
    {
        key: 'mailchimp',
        category: 'marketing',
        image: mailchimp,
        fieldsComponent: MailchimpFields,
        superadminOnly: false,
        tags: ['newsletter'],
    },
    {
        key: 'brevo',
        category: 'marketing',
        image: brevo,
        fieldsComponent: BrevoFields,
        superadminOnly: false,
        tags: ['newsletter'],
    },
    {
        key: 'getresponse',
        category: 'marketing',
        image: getresponse,
        fieldsComponent: GetResponseFields,
        superadminOnly: false,
        tags: ['newsletter'],
    },
    {
        key: 'gtm',
        category: 'marketing',
        image: gtm,
        fieldsComponent: GoogleTagManagerFields,
        superadminOnly: false,
        tags: ['analytics'],
    },
    {
        key: 'posthog',
        category: 'marketing',
        image: posthog,
        fieldsComponent: PostHogFields,
        superadminOnly: false,
        tags: ['analytics'],
    },
    {
        key: 'recaptcha',
        category: 'marketing',
        image: recaptcha,
        fieldsComponent: GoogleRecaptchaFields,
        superadminOnly: false,
        tags: ['security'],
    },
    {
        key: 'maxmind',
        category: 'web',
        image: maxmind,
        fieldsComponent: MaxmindFields,
        superadminOnly: false,
        tags: ['geoblocking', 'security'],
    },
    {
        key: 'wp',
        category: 'web',
        image: wp,
        fieldsComponent: WordpressFields,
        superadminOnly: false,
        tags: ['website'],
    },
    {
        key: 'weserv',
        category: 'web',
        image: weserv,
        fieldsComponent: WeservFields,
        superadminOnly: true,
        tags: ['technical'],
    },
    {
        key: 'plausible',
        category: 'marketing',
        image: plausible,
        fieldsComponent: PlausibleFields,
        superadminOnly: false,
        tags: ['analytics', 'traffic'],
    },
];

export const get_integration_entry = (key) => (
    INTEGRATION_CATALOG.find(entry => entry.key === key)
);

export const get_visible_catalog = (is_superadmin) => (
    INTEGRATION_CATALOG.filter(entry => !entry.superadminOnly || is_superadmin)
);

export const get_integration_status_summary = integration_status_summary;
export { is_integration_configured };

export const get_enabled_integrations = (integrations, is_superadmin) => {
    const visible_keys = new Set(
        get_visible_catalog(is_superadmin).map(entry => entry.key)
    );

    return Object.keys(integrations || {})
        .filter(key => visible_keys.has(key))
        .map(key => ({
            ...get_integration_entry(key),
            key,
            config: integrations[key],
        }))
        .filter(entry => entry.image);
};

export const get_catalog_integrations = (integrations, is_superadmin, category = null) => {
    const enabled_keys = new Set(Object.keys(integrations || {}));

    return get_visible_catalog(is_superadmin)
        .filter(entry => !enabled_keys.has(entry.key))
        .filter(entry => !category || entry.category === category);
};

export const render_integration_fields = (key, props = {}) => {
    const entry = get_integration_entry(key);
    if (!entry?.fieldsComponent)
        return null;

    const FieldsComponent = entry.fieldsComponent;
    return <FieldsComponent {...props} />;
};
